import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { housesAPI } from '../api';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../shared/constants/api';
import { useTranslation } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import logo from '../logo.svg';
import './Dashboard.css';

const API_URL = API_CONFIG.BASE_URL;

function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ title: '', message: '', onConfirm: null, type: 'warning' });
  const [successMessage, setSuccessMessage] = useState('');
  const [createdInviteCode, setCreatedInviteCode] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [transferHouseData, setTransferHouseData] = useState(null);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState('');
  const [houseMembers, setHouseMembers] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showSpendingLimitsModal, setShowSpendingLimitsModal] = useState(false);
  const [spendingLimits, setSpendingLimits] = useState([]);
  const [limitForm, setLimitForm] = useState({ category: '', limit_amount: '' });
  const [limitError, setLimitError] = useState('');
  const [limitSuccess, setLimitSuccess] = useState('');
  const [profileTab, setProfileTab] = useState('info');
  const [upsellModal, setUpsellModal] = useState({ show: false, scenario: null });
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadGroups();
    // İlk giriş karşılama - sadece ücretsiz kullanıcılara, sadece bir kez
    if (!user?.is_pro) {
      const seen = localStorage.getItem('pro_welcome_seen');
      if (!seen) {
        setTimeout(() => setShowWelcomeModal(true), 1200);
      }
    }
  }, []);

  const loadGroups = async () => {
    try {
      const data = await housesAPI.getMyHouses();
      const groupsArray = Array.isArray(data) ? data : [];
      setGroups(groupsArray);
    } catch (error) {
      console.error('Groups load error:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');

    if (!newGroupName.trim()) {
      setError(t('dashboard.createModal.emptyError'));
      return;
    }

    try {
      const response = await housesAPI.createHouse({ name: newGroupName });
      const newHouse = response;

      setGroups([...groups, newHouse]);
      setNewGroupName('');
      setShowCreateModal(false);

      setCreatedInviteCode(newHouse.invite_code);
      setSuccessMessage(t('dashboard.successModal.houseCreated', { name: newHouse.name }));
      setShowSuccessModal(true);

      await loadGroups();
    } catch (error) {
      if (error.response?.data?.upgrade_required) {
        setShowCreateModal(false);
        showUpsell('houseLimit');
      } else {
        setError(error.response?.data?.error || t('dashboard.createModal.error'));
      }
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    setError('');

    if (!inviteCode.trim()) {
      setError(t('dashboard.joinModal.emptyError'));
      return;
    }

    try {
      const response = await housesAPI.joinHouse({ invite_code: inviteCode });
      const newHouse = response;

      setGroups([...groups, newHouse]);
      setInviteCode('');
      setShowJoinModal(false);
      alert(t('dashboard.joinModal.success', { name: newHouse.name }));
    } catch (error) {
      if (error.response?.data?.upgrade_required) {
        setShowJoinModal(false);
        showUpsell('houseLimit');
      } else {
        setError(error.response?.data?.error || t('dashboard.joinModal.error'));
      }
    }
  };

  const handleSelectGroup = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  const handleLogout = () => {
    setConfirmData({
      title: `👋 ${t('dashboard.logoutConfirm.title')}`,
      message: t('dashboard.logoutConfirm.message'),
      type: 'warning',
      onConfirm: () => {
        logout();
        navigate('/');
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteHouse = async (houseId, houseName) => {
    setConfirmData({
      title: `🗑️ ${t('dashboard.deleteHouse.title')}`,
      message: t('dashboard.deleteHouse.message', { name: houseName }),
      type: 'danger',
      onConfirm: () => confirmDeleteHouse(houseId, houseName)
    });
    setShowConfirmModal(true);
  };

  const confirmDeleteHouse = async (houseId, houseName) => {
    setShowConfirmModal(false);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/houses/${houseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('dashboard.deleteHouse.error'));
      }

      setGroups(groups.filter(g => g.id !== houseId));
      setSuccessMessage(t('dashboard.deleteHouse.success', { name: houseName }));
      setShowSuccessModal(true);
    } catch (error) {
      setConfirmData({
        title: `❌ ${t('common.error')}`,
        message: error.message || t('dashboard.deleteHouse.error'),
        type: 'error',
        onConfirm: () => setShowConfirmModal(false),
        singleButton: true
      });
      setShowConfirmModal(true);
    }
  };

  const handleLeaveHouse = async (houseId, houseName, memberCount) => {
    if (memberCount === 1) {
      return handleDeleteHouse(houseId, houseName);
    }

    const house = groups.find(g => g.id === houseId);

    if (house.role === 'admin') {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/houses/${houseId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (response.ok) {
          const otherMembers = data.members.filter(m => m.id !== user?.id);

          if (otherMembers.length === 0) {
            alert(`❌ ${t('dashboard.leaveHouse.noMembersToTransfer')}`);
            return;
          }

          setTransferHouseData({ id: houseId, name: houseName });
          setHouseMembers(otherMembers);
          setSelectedNewAdmin('');
          setShowTransferModal(true);
        }
      } catch (error) {
        alert(`❌ ${t('dashboard.leaveHouse.loadMembersError')}: ${error.message}`);
      }
      return;
    }

    setConfirmData({
      title: `🚪 ${t('dashboard.leaveHouse.title')}`,
      message: t('dashboard.leaveHouse.message', { name: houseName }),
      type: 'warning',
      onConfirm: () => confirmLeaveHouse(houseId, houseName)
    });
    setShowConfirmModal(true);
    return;
  };

  const confirmLeaveHouse = async (houseId, houseName) => {
    setShowConfirmModal(false);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/houses/${houseId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('dashboard.leaveHouse.error'));
      }

      setGroups(groups.filter(g => g.id !== houseId));
      setSuccessMessage(t('dashboard.leaveHouse.success', { name: houseName }));
      setShowSuccessModal(true);
    } catch (error) {
      setConfirmData({
        title: `❌ ${t('common.error')}`,
        message: error.message || t('dashboard.leaveHouse.error'),
        type: 'error',
        onConfirm: () => setShowConfirmModal(false),
        singleButton: true
      });
      setShowConfirmModal(true);
    }
  };

  const handleTransferAndLeave = async () => {
    if (!selectedNewAdmin) {
      alert(`⚠️ ${t('transfer.selectAdminWarning')}`);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');

      const roleResponse = await fetch(
        `${API_URL}/houses/${transferHouseData.id}/members/${selectedNewAdmin}/role`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ role: 'admin' })
        }
      );

      if (!roleResponse.ok) {
        const data = await roleResponse.json();
        throw new Error(data.error || t('transfer.transferFailed'));
      }

      const leaveResponse = await fetch(
        `${API_URL}/houses/${transferHouseData.id}/leave`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!leaveResponse.ok) {
        const data = await leaveResponse.json();
        throw new Error(data.error || t('dashboard.leaveHouse.error'));
      }

      setShowTransferModal(false);
      setShowProfileModal(false);
      setGroups(groups.filter(g => g.id !== transferHouseData.id));
      alert(`✅ ${t('transfer.success')}`);
    } catch (error) {
      alert('❌ ' + error.message);
    }
  };

  const handleShowProfile = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
    setProfileTab('info');
    setShowProfileModal(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');

    if (!profileData.name.trim()) {
      setError(t('profile.nameEmpty'));
      return;
    }

    const hasPassword = user?.has_password;

    if (hasPassword) {
      // Şifreli hesap: değiştirmek istiyorsa mevcut şifre gerekli
      if (profileData.newPassword || profileData.currentPassword) {
        if (!profileData.currentPassword) {
          setError(t('profile.currentPasswordRequired'));
          return;
        }
        if (!profileData.newPassword) {
          setError(t('profile.newPasswordEmpty'));
          return;
        }
        if (profileData.newPassword !== profileData.confirmPassword) {
          setError(t('profile.newPasswordMismatch'));
          return;
        }
        if (profileData.newPassword.length < 6) {
          setError(t('profile.newPasswordTooShort'));
          return;
        }
      }
    } else {
      // OAuth hesabı: şifre ekliyorsa sadece yeni şifre gerekli
      if (profileData.newPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          setError(t('profile.newPasswordMismatch'));
          return;
        }
        if (profileData.newPassword.length < 6) {
          setError(t('profile.newPasswordTooShort'));
          return;
        }
      }
    }

    try {
      const token = localStorage.getItem('auth_token');

      // OAuth hesabına şifre ekleniyor
      if (!hasPassword && profileData.newPassword) {
        const pwResponse = await fetch(`${API_URL.replace('/api', '')}/api/auth/set-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ password: profileData.newPassword })
        });
        const pwData = await pwResponse.json();
        if (!pwResponse.ok) {
          throw new Error(pwData.error || t('profile.updateError'));
        }
      }

      const updateData = { name: profileData.name };

      if (hasPassword && profileData.currentPassword && profileData.newPassword) {
        updateData.current_password = profileData.currentPassword;
        updateData.new_password = profileData.newPassword;
      }

      const response = await fetch(`${API_URL.replace('/api', '')}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.updateError'));
      }

      updateUser(data.user);
      setShowProfileModal(false);
      alert(t('profile.updateSuccess'));
    } catch (error) {
      setError(error.message || t('profile.updateError'));
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('profile.deleteAccountConfirm'))) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/auth/account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        logout();
      } else {
        const data = await response.json();
        alert(data.error || t('common.networkError'));
      }
    } catch {
      alert(t('common.networkError'));
    }
  };

  const handleUpgradePro = (plan) => {
    const isYearly = plan === 'yearly';
    const price = isYearly ? t('pro.yearlyPrice') : t('pro.monthlyPrice');
    const planName = isYearly ? t('pro.yearly') : t('pro.monthly');
    setConfirmData({
      title: `⭐ ${planName} — ${price}`,
      message: `${planName} planına geçmek istediğinizden emin misiniz? ${price} ücret tahsil edilecektir.`,
      type: 'warning',
      upgradeConfirm: true,
      onConfirm: () => confirmUpgradePro(plan),
    });
    setShowConfirmModal(true);
  };

  const confirmUpgradePro = async (plan) => {
    setUpgradeLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/auth/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan })
      });
      const data = await response.json();
      if (response.ok) {
        updateUser({ ...user, is_pro: true, pro_since: data.pro_since });
        setShowUpgradeModal(false);
        setSuccessMessage(t('pro.activated'));
        setCreatedInviteCode('');
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setUpgradeLoading(false);
    }
  };

  const loadSpendingLimits = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/spending-limits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSpendingLimits(data);
      }
    } catch (err) {
      console.error('Limits load error:', err);
    }
  };

  const handleSaveLimit = async (e) => {
    e.preventDefault();
    setLimitError('');
    setLimitSuccess('');
    if (!limitForm.category || !limitForm.limit_amount) {
      setLimitError(t('limits.category') + ' ve ' + t('limits.monthlyLimit') + ' zorunludur.');
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/spending-limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category: limitForm.category, limit_amount: parseFloat(limitForm.limit_amount) })
      });
      if (response.ok) {
        setLimitForm({ category: '', limit_amount: '' });
        setLimitSuccess(t('limits.saved'));
        loadSpendingLimits();
      }
    } catch (err) {
      setLimitError(t('common.error'));
    }
  };

  // --- PRO UPSELL SENARYOLARI ---
  const upsellScenarios = {
    houseLimit: {
      icon: '🏠',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      titleKey: 'upsell.houseLimit.title',
      subtitleKey: 'upsell.houseLimit.subtitle',
      features: ['pro.feature1', 'pro.feature2', 'pro.feature3'],
      highlight: 'pro.feature1',
    },
    spendingLimits: {
      icon: '📊',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      titleKey: 'upsell.spendingLimits.title',
      subtitleKey: 'upsell.spendingLimits.subtitle',
      features: ['pro.feature2', 'pro.feature3', 'pro.feature4'],
      highlight: 'pro.feature2',
    },
    exportData: {
      icon: '📥',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      titleKey: 'upsell.export.title',
      subtitleKey: 'upsell.export.subtitle',
      features: ['upsell.export.feature', 'pro.feature1', 'pro.feature4'],
      highlight: 'upsell.export.feature',
    },
    analytics: {
      icon: '📈',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      titleKey: 'upsell.analytics.title',
      subtitleKey: 'upsell.analytics.subtitle',
      features: ['upsell.analytics.feature', 'pro.feature2', 'pro.feature3'],
      highlight: 'upsell.analytics.feature',
    },
    softReminder: {
      icon: '⭐',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      titleKey: 'upsell.reminder.title',
      subtitleKey: 'upsell.reminder.subtitle',
      features: ['pro.feature1', 'pro.feature2', 'pro.feature3', 'pro.feature4'],
      highlight: null,
    },
  };

  const showUpsell = (scenario) => {
    if (user?.is_pro) return;
    setUpsellModal({ show: true, scenario });
  };

  // Periyodik soft reminder - her 5. oturumda
  useEffect(() => {
    if (!user?.is_pro) {
      const count = parseInt(localStorage.getItem('monnaify_session_count') || '0') + 1;
      localStorage.setItem('monnaify_session_count', String(count));
      if (count > 1 && count % 5 === 0) {
        const lastReminder = localStorage.getItem('pro_soft_reminder_last');
        const now = Date.now();
        if (!lastReminder || now - parseInt(lastReminder) > 86400000) { // 24 saat
          setTimeout(() => {
            if (!showWelcomeModal) {
              showUpsell('softReminder');
              localStorage.setItem('pro_soft_reminder_last', String(now));
            }
          }, 3000);
        }
      }
    }
  }, []);

  // Spending limitlere tıklama (free user)
  const handleLimitsClick = () => {
    if (user?.is_pro) {
      loadSpendingLimits();
      setShowSpendingLimitsModal(true);
    } else {
      showUpsell('spendingLimits');
    }
  };

  // Export butonuna tıklama (free user)
  const handleExportClick = () => {
    if (user?.is_pro) {
      // TODO: gerçek export işlemi
      alert('Export özelliği yakında!');
    } else {
      showUpsell('exportData');
    }
  };

  // Analytics butonuna tıklama (free user)
  const handleAnalyticsClick = () => {
    if (user?.is_pro) {
      // TODO: gerçek analytics sayfası
      alert('Analiz sayfası yakında!');
    } else {
      showUpsell('analytics');
    }
  };

  const handleDeleteLimit = async (limitId) => {
    if (!window.confirm(t('limits.deleteConfirm'))) return;
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/spending-limits/${limitId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSpendingLimits(spendingLimits.filter(l => l.id !== limitId));
    } catch (err) {
      console.error('Delete limit error:', err);
    }
  };

  const getRoleName = (role) => {
    const roleMap = {
      admin: t('dashboard.roles.admin'),
      moderator: t('dashboard.roles.moderator'),
      member: t('dashboard.roles.member')
    };
    return roleMap[role] || role;
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-logo">
            <img src={logo} alt="Monnaify" className="logo-img" />
            <h1>Monnaify</h1>
          </div>
          <div className="user-info">
            <LanguageSwitcher />
            <div className="header-actions">
              <button onClick={handleShowProfile} className="btn-profile-pill">
                <div className="btn-profile-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
                <span className="btn-profile-label">{t('dashboard.profile')}</span>
                <span className={`btn-profile-tier ${user?.is_pro ? 'pro' : 'free'}`}>
                  {user?.is_pro ? 'PRO' : t('pro.free')}
                </span>
              </button>
              <button onClick={handleLogout} className="btn-header btn-logout" title={t('dashboard.logout')}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Pro Banner - sadece ücretsiz kullanıcılara */}
      {!user?.is_pro && (
        <div className="pro-banner" onClick={() => setShowUpgradeModal(true)}>
          <span className="pro-banner-icon">⭐</span>
          <span className="pro-banner-text">
            <strong>Monnaify Pro</strong> — Sınırsız ev, kategori limitleri ve daha fazlası
          </span>
          <span className="pro-banner-cta">Hemen Geç →</span>
        </div>
      )}

      <div className="dashboard-content">
        <div className="actions-bar">
          <button onClick={() => setShowCreateModal(true)} className="btn btn-create">
            {t('dashboard.createHouse')}
          </button>
          <button onClick={() => setShowJoinModal(true)} className="btn btn-join">
            🔗 {t('dashboard.joinHouse')}
          </button>
          <button onClick={handleLimitsClick} className={`btn ${user?.is_pro ? 'btn-limits' : 'btn-limits-locked'}`}>
            📊 {t('pro.manageLimits')} {!user?.is_pro && <span className="lock-icon">🔒</span>}
          </button>
          <button onClick={handleExportClick} className={`btn ${user?.is_pro ? 'btn-export' : 'btn-export-locked'}`}>
            📥 {t('upsell.export.btn') || 'Dışa Aktar'} {!user?.is_pro && <span className="lock-icon">🔒</span>}
          </button>
          <button onClick={handleAnalyticsClick} className={`btn ${user?.is_pro ? 'btn-analytics' : 'btn-analytics-locked'}`}>
            📈 {t('upsell.analytics.btn') || 'Detaylı Analiz'} {!user?.is_pro && <span className="lock-icon">🔒</span>}
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="empty-state">
            <h2>{t('dashboard.emptyState.title')}</h2>
            <p>{t('dashboard.emptyState.description')}</p>
          </div>
        ) : (
          <div className="groups-grid">
            {groups.map((group) => (
              <div
                key={group.id}
                className="group-card"
                onClick={() => handleSelectGroup(group.id)}
              >
                <h3>{group.name}</h3>
                <div className="group-info">
                  <p className="invite-code">{t('dashboard.inviteCode')}: <strong>{group.invite_code}</strong></p>
                  <p className="member-count">
                    👥 {group.member_count || 0} {t('dashboard.members')}
                  </p>
                  {group.role && (
                    <span className={`role-badge role-${group.role}`}>
                      {getRoleName(group.role)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ev Oluştur Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('dashboard.createModal.title')}</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleCreateGroup}>
              <div className="form-group">
                <label>{t('dashboard.createModal.houseName')}</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('dashboard.createModal.placeholder')}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Eve Katıl Modal */}
      {showJoinModal && (
        <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('dashboard.joinModal.title')}</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleJoinGroup}>
              <div className="form-group">
                <label>{t('dashboard.joinModal.inviteCode')}</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder={t('dashboard.joinModal.placeholder')}
                  maxLength={6}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowJoinModal(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.join')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal modal-profile" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header profile-header">
              <div className="profile-header-content">
                <div className="profile-avatar">
                  {user?.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="profile-header-info">
                  <div className="profile-header-name-row">
                    <h2>{user?.name}</h2>
                    {user?.is_pro && <span className="pro-badge-header">{t('pro.badge')}</span>}
                  </div>
                  <p className="profile-subtitle">{user?.email}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            </div>

            {/* Tab Bar */}
            <div className="profile-tab-bar">
              {[
                { key: 'info', emoji: '👤', label: t('profile.tabInfo') },
                { key: 'subscription', emoji: '⭐', label: t('profile.tabSubscription') },
                { key: 'houses', emoji: '🏠', label: t('profile.tabHouses') },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`profile-tab-item${profileTab === tab.key ? ' active' : ''}`}
                  onClick={() => setProfileTab(tab.key)}
                  type="button"
                >
                  <span className="tab-emoji">{tab.emoji}</span>
                  <span className="tab-label">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab: Kişisel Bilgiler */}
            {profileTab === 'info' && (
              <form onSubmit={handleUpdateProfile} className="profile-form">
                {error && <div className="error-message">⚠️ {error}</div>}

                <div className="form-section">
                  <div className="section-icon">👤</div>
                  <div className="section-content">
                    <h3>{t('profile.personalInfo')}</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <span className="label-icon">📝</span>
                          {t('profile.name')}
                        </label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                          placeholder={t('profile.namePlaceholder')}
                          required
                          className="input-with-icon"
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <span className="label-icon">📧</span>
                          {t('profile.email')}
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          placeholder={t('auth.login.emailPlaceholder')}
                          className="input-with-icon"
                          disabled
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', opacity: 0.7 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section password-section">
                  <div className="section-icon">🔒</div>
                  <div className="section-content">
                    <div className="section-header">
                      <h3>{user?.has_password ? t('profile.changePassword') : t('profile.addPassword')}</h3>
                      <span className="optional-badge">{t('common.optional')}</span>
                    </div>
                    <p className="form-hint">
                      {user?.has_password
                        ? `💡 ${t('profile.passwordHint')}`
                        : `🔗 ${t('profile.addPasswordHint')}`}
                    </p>

                    {user?.has_password && (
                      <div className="form-group">
                        <label>
                          <span className="label-icon">🔑</span>
                          {t('profile.currentPassword')}
                        </label>
                        <input
                          type="password"
                          value={profileData.currentPassword}
                          onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
                          placeholder="••••••••"
                          className="input-with-icon"
                        />
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>
                          <span className="label-icon">✨</span>
                          {t('profile.newPassword')}
                        </label>
                        <input
                          type="password"
                          value={profileData.newPassword}
                          onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
                          placeholder={t('profile.newPasswordPlaceholder')}
                          className="input-with-icon"
                        />
                        {profileData.newPassword && profileData.newPassword.length < 6 && (
                          <span className="input-hint error">{t('profile.minChars')}</span>
                        )}
                        {profileData.newPassword && profileData.newPassword.length >= 6 && (
                          <span className="input-hint success">✓ {t('profile.strongPassword')}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>
                          <span className="label-icon">✅</span>
                          {t('profile.confirmNewPassword')}
                        </label>
                        <input
                          type="password"
                          value={profileData.confirmPassword}
                          onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
                          placeholder={t('profile.confirmNewPasswordPlaceholder')}
                          className="input-with-icon"
                        />
                        {profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword && (
                          <span className="input-hint error">{t('profile.passwordsDontMatch')}</span>
                        )}
                        {profileData.confirmPassword && profileData.newPassword === profileData.confirmPassword && profileData.newPassword.length >= 6 && (
                          <span className="input-hint success">✓ {t('profile.passwordsMatch')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-actions profile-actions">
                  <button type="button" onClick={() => setShowProfileModal(false)} className="btn btn-cancel">
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-save">
                    💾 {t('profile.saveChanges')}
                  </button>
                </div>
                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                  >
                    🗑️ {t('profile.deleteAccount')}
                  </button>
                </div>
              </form>
            )}

            {/* Tab: Abonelik */}
            {profileTab === 'subscription' && (
              <div className="profile-tab-content">
                {user?.is_pro ? (
                  <div className="pro-active-card">
                    <div className="pro-active-icon">⭐</div>
                    <div className="pro-active-title">{t('pro.activeTitle')}</div>
                    <div className="pro-active-subtitle">
                      {t('pro.activeSince')}: {user.pro_since ? new Date(user.pro_since).toLocaleDateString() : '—'}
                    </div>
                    <ul className="upgrade-features pro-active-features">
                      <li>✅ {t('pro.feature1')}</li>
                      <li>✅ {t('pro.feature2')}</li>
                      <li>✅ {t('pro.feature3')}</li>
                      <li>✅ {t('pro.feature4')}</li>
                    </ul>
                    <button
                      className="btn-limits"
                      onClick={() => { loadSpendingLimits(); setShowProfileModal(false); setShowSpendingLimitsModal(true); }}
                    >
                      📊 {t('pro.manageLimits')}
                    </button>
                  </div>
                ) : (
                  <div className="upgrade-inline">
                    <div className="upgrade-hero">
                      <div className="upgrade-icon">⭐</div>
                      <h3>{t('pro.upgradeTitle')}</h3>
                      <p className="upgrade-subtitle">{t('pro.upgradeSubtitle')}</p>
                    </div>
                    <ul className="upgrade-features">
                      <li>✅ {t('pro.feature1')}</li>
                      <li>✅ {t('pro.feature2')}</li>
                      <li>✅ {t('pro.feature3')}</li>
                      <li>✅ {t('pro.feature4')}</li>
                    </ul>
                    <div className="upgrade-plans">
                      <button
                        className="plan-card plan-monthly"
                        onClick={() => handleUpgradePro('monthly')}
                        disabled={upgradeLoading}
                      >
                        <span className="plan-name">{t('pro.monthly')}</span>
                        <span className="plan-price">{t('pro.monthlyPrice')}</span>
                      </button>
                      <button
                        className="plan-card plan-yearly"
                        onClick={() => handleUpgradePro('yearly')}
                        disabled={upgradeLoading}
                      >
                        <span className="plan-badge">{t('pro.yearlySave')}</span>
                        <span className="plan-name">{t('pro.yearly')}</span>
                        <span className="plan-price">{t('pro.yearlyPrice')}</span>
                      </button>
                    </div>
                    {upgradeLoading && <p className="upgrade-loading">{t('pro.activating')}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Evlerim */}
            {profileTab === 'houses' && (
              <div className="profile-tab-content">
                <div className="section-content">
                  <h3 style={{ marginBottom: 12 }}>{t('profile.myHouses')}</h3>
                  {groups.length === 0 ? (
                    <p className="no-houses">{t('profile.noHouses')}</p>
                  ) : (
                    <div className="houses-list">
                      {groups.map((house) => (
                        <div key={house.id} className="house-item">
                          <div className="house-item-info">
                            <div className="house-item-header">
                              <span className="house-name">{house.name}</span>
                              <span className={`role-badge role-${house.role}`}>
                                {house.role === 'admin' ? `👑 ${t('dashboard.roles.admin')}` :
                                 house.role === 'moderator' ? `⭐ ${t('dashboard.roles.moderator')}` : `👤 ${t('dashboard.roles.member')}`}
                              </span>
                            </div>
                            <div className="house-item-meta">
                              <span>👥 {house.member_count || 0} {t('dashboard.members')}</span>
                              <span>📅 {t('dashboard.inviteCode')}: {house.invite_code}</span>
                            </div>
                          </div>
                          <div className="house-item-actions">
                            {house.member_count === 1 ? (
                              <button
                                type="button"
                                onClick={() => handleDeleteHouse(house.id, house.name)}
                                className="btn-house-delete"
                                title={t('profile.deleteHouse')}
                              >
                                🗑️ {t('profile.deleteHouse')}
                              </button>
                            ) : house.role === 'admin' ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteHouse(house.id, house.name)}
                                  className="btn-house-delete"
                                  title={t('profile.deleteHouse')}
                                >
                                  🗑️ {t('common.delete')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleLeaveHouse(house.id, house.name, house.member_count)}
                                  className="btn-house-leave"
                                  title={t('profile.leave')}
                                >
                                  🚪 {t('profile.leave')}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleLeaveHouse(house.id, house.name, house.member_count)}
                                className="btn-house-leave"
                                title={t('profile.leave')}
                              >
                                🚪 {t('profile.leave')}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer Admin Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal modal-transfer" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header transfer-header">
              <h2>👑 {t('transfer.title')}</h2>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="transfer-info">
                <p className="transfer-message">
                  {t('transfer.message', { name: transferHouseData?.name })}
                </p>
                <div className="transfer-warning">
                  ⚠️ {t('transfer.warning')}
                </div>
              </div>

              <div className="form-group">
                <label>
                  <span className="label-icon">👤</span>
                  {t('transfer.selectNewAdmin')}
                </label>
                <select
                  value={selectedNewAdmin}
                  onChange={(e) => setSelectedNewAdmin(e.target.value)}
                  className="member-select"
                  required
                >
                  <option value="">{t('transfer.selectPlaceholder')}</option>
                  {houseMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({member.role === 'moderator' ? t('dashboard.roles.moderator') : t('dashboard.roles.member')})
                    </option>
                  ))}
                </select>
              </div>

              {selectedNewAdmin && (
                <div className="transfer-preview">
                  <div className="preview-item">
                    <span className="preview-label">{t('transfer.selectedMember')}:</span>
                    <strong>{houseMembers.find(m => m.id === parseInt(selectedNewAdmin))?.name}</strong>
                  </div>
                  <div className="preview-item">
                    <span className="preview-label">{t('transfer.newRole')}:</span>
                    <span className="role-badge role-admin">👑 {t('dashboard.roles.admin')}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="btn btn-cancel"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleTransferAndLeave}
                className="btn btn-primary"
                disabled={!selectedNewAdmin}
              >
                {t('transfer.transferAndLeave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM MODAL */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => confirmData.singleButton && setShowConfirmModal(false)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-header-${confirmData.type}`}>
              <h2>{confirmData.title}</h2>
            </div>

            <div className="modal-body">
              <p className="confirm-message">{confirmData.message}</p>
            </div>

            <div className="modal-actions">
              {!confirmData.singleButton && (
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="btn btn-cancel"
                >
                  {t('common.cancel')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmData.onConfirm) confirmData.onConfirm();
                }}
                className={`btn btn-${confirmData.type === 'danger' ? 'danger' : confirmData.type === 'error' ? 'primary' : 'warning'}`}
              >
                {confirmData.singleButton ? t('common.ok') :
                 confirmData.upgradeConfirm ? `⭐ ${t('pro.upgradeButton')}` :
                 confirmData.type === 'danger' ? t('dashboard.confirmYesDelete') :
                 confirmData.title?.includes(t('dashboard.logoutConfirm.title')) ? t('dashboard.logoutConfirm.confirm') :
                 t('dashboard.confirmYesLeave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content modal-success" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-header-success">
              <h2>✅ {t('common.success')}</h2>
            </div>

            <div className="modal-body">
              <p className="success-message">{successMessage}</p>

              {createdInviteCode && (
                <div className="invite-code-display">
                  <label>📋 {t('dashboard.successModal.inviteCode')}</label>
                  <div className="invite-code-box">
                    <code>{createdInviteCode}</code>
                    <button
                      type="button"
                      className={`btn-copy ${isCopied ? 'copied' : ''}`}
                      onClick={() => {
                        navigator.clipboard.writeText(createdInviteCode);
                        setIsCopied(true);
                        setTimeout(() => setIsCopied(false), 2000);
                      }}
                    >
                      {isCopied ? `✅ ${t('dashboard.successModal.copied')}` : `📋 ${t('dashboard.successModal.copy')}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedInviteCode('');
                  setIsCopied(false);
                }}
                className="btn btn-primary"
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOŞGELDİN / WELCOME MODAL */}
      {showWelcomeModal && (
        <div className="modal-overlay welcome-overlay">
          <div className="modal-welcome" onClick={(e) => e.stopPropagation()}>
            <div className="welcome-top">
              <div className="welcome-logo">🏠</div>
              <h2 className="welcome-title">Monnaify'a Hoş Geldin!</h2>
              <p className="welcome-sub">Ev harcamalarını birlikte yönetmenin en kolay yolu</p>
            </div>

            <div className="welcome-features">
              <div className="welcome-feature">
                <span className="wf-icon">🏠</span>
                <div><strong>Ev Yönetimi</strong><p>Ücretsiz planda 1 ev oluşturabilirsin</p></div>
              </div>
              <div className="welcome-feature welcome-feature-pro">
                <span className="wf-icon">⭐</span>
                <div>
                  <strong>Pro ile Sınırsız</strong>
                  <p>Sınırsız ev, kategori bazlı harcama limitleri ve uyarılar</p>
                </div>
                <span className="wf-pro-tag">PRO</span>
              </div>
              <div className="welcome-feature">
                <span className="wf-icon">👥</span>
                <div><strong>Birlikte Takip</strong><p>Ev arkadaşlarınla harcamaları paylaş</p></div>
              </div>
            </div>

            <div className="welcome-actions">
              <button
                className="btn-welcome-pro"
                onClick={() => { setShowWelcomeModal(false); setShowUpgradeModal(true); }}
              >
                ⭐ Pro'yu Keşfet — ₺39/ay
              </button>
              <button
                className="btn-welcome-skip"
                onClick={() => { localStorage.setItem('pro_welcome_seen', '1'); setShowWelcomeModal(false); }}
              >
                Şimdilik ücretsiz devam et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRO UPSELL MODAL — Senaryo bazlı */}
      {upsellModal.show && upsellScenarios[upsellModal.scenario] && (() => {
        const s = upsellScenarios[upsellModal.scenario];
        return (
          <div className="upsell-overlay" onClick={() => setUpsellModal({ show: false, scenario: null })}>
            <div className="upsell-modal" onClick={(e) => e.stopPropagation()}>
              <button className="upsell-close" onClick={() => setUpsellModal({ show: false, scenario: null })}>×</button>

              <div className="upsell-hero" style={{ background: s.gradient }}>
                <div className="upsell-hero-icon">{s.icon}</div>
                <h2 className="upsell-hero-title">{t(s.titleKey)}</h2>
                <p className="upsell-hero-subtitle">{t(s.subtitleKey)}</p>
              </div>

              <div className="upsell-body">
                <div className="upsell-features">
                  {s.features.map((f, i) => (
                    <div key={i} className={`upsell-feature-item${f === s.highlight ? ' highlighted' : ''}`}>
                      <span className="upsell-feature-check">{f === s.highlight ? '⭐' : '✅'}</span>
                      <span className="upsell-feature-text">{t(f)}</span>
                    </div>
                  ))}
                </div>

                <div className="upsell-plans">
                  <button
                    className="upsell-plan upsell-plan-monthly"
                    onClick={() => { setUpsellModal({ show: false, scenario: null }); handleUpgradePro('monthly'); }}
                  >
                    <span className="upsell-plan-name">{t('pro.monthly')}</span>
                    <span className="upsell-plan-price">{t('pro.monthlyPrice')}</span>
                  </button>
                  <button
                    className="upsell-plan upsell-plan-yearly"
                    onClick={() => { setUpsellModal({ show: false, scenario: null }); handleUpgradePro('yearly'); }}
                  >
                    <span className="upsell-plan-badge">{t('pro.yearlySave')}</span>
                    <span className="upsell-plan-name">{t('pro.yearly')}</span>
                    <span className="upsell-plan-price">{t('pro.yearlyPrice')}</span>
                  </button>
                </div>

                <button
                  className="upsell-skip"
                  onClick={() => setUpsellModal({ show: false, scenario: null })}
                >
                  {t('upsell.maybeLater')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* PRO UPGRADE MODAL */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal modal-upgrade" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            <div className="upgrade-hero">
              <div className="upgrade-icon">⭐</div>
              <h2>{t('pro.upgradeTitle')}</h2>
              <p className="upgrade-subtitle">{t('pro.upgradeSubtitle')}</p>
            </div>

            <ul className="upgrade-features">
              <li>✅ {t('pro.feature1')}</li>
              <li>✅ {t('pro.feature2')}</li>
              <li>✅ {t('pro.feature3')}</li>
              <li>✅ {t('pro.feature4')}</li>
            </ul>

            <div className="upgrade-plans">
              <button
                className="plan-card plan-monthly"
                onClick={() => handleUpgradePro('monthly')}
                disabled={upgradeLoading}
              >
                <span className="plan-name">{t('pro.monthly')}</span>
                <span className="plan-price">{t('pro.monthlyPrice')}</span>
              </button>
              <button
                className="plan-card plan-yearly"
                onClick={() => handleUpgradePro('yearly')}
                disabled={upgradeLoading}
              >
                <span className="plan-badge">{t('pro.yearlySave')}</span>
                <span className="plan-name">{t('pro.yearly')}</span>
                <span className="plan-price">{t('pro.yearlyPrice')}</span>
              </button>
            </div>
            {upgradeLoading && <p className="upgrade-loading">{t('pro.activating')}</p>}
          </div>
        </div>
      )}

      {/* SPENDING LIMITS MODAL */}
      {showSpendingLimitsModal && (
        <div className="modal-overlay" onClick={() => setShowSpendingLimitsModal(false)}>
          <div className="modal modal-limits" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 {t('limits.title')}</h2>
              <button className="modal-close" onClick={() => setShowSpendingLimitsModal(false)}>×</button>
            </div>
            <p className="limits-subtitle">{t('limits.subtitle')}</p>

            {/* Mevcut limitler */}
            {spendingLimits.length === 0 ? (
              <div className="limits-empty">
                <p>{t('limits.empty')}</p>
                <p className="limits-empty-hint">{t('limits.emptyHint')}</p>
              </div>
            ) : (
              <div className="limits-list">
                {spendingLimits.map(lim => (
                  <div key={lim.id} className={`limit-item ${lim.usage_percent >= 100 ? 'limit-exceeded' : ''}`}>
                    <div className="limit-item-header">
                      <span className="limit-category">{lim.category}</span>
                      <button className="btn-limit-delete" onClick={() => handleDeleteLimit(lim.id)}>🗑️</button>
                    </div>
                    <div className="limit-progress-bar">
                      <div className="limit-progress-fill" style={{ width: `${Math.min(lim.usage_percent, 100)}%` }} />
                    </div>
                    <div className="limit-item-footer">
                      <span>{t('limits.currentMonth')}: ₺{lim.current_month_total}</span>
                      <span>/ ₺{lim.limit_amount} {lim.usage_percent >= 100 ? t('limits.exceeded') : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Yeni limit ekle */}
            <form onSubmit={handleSaveLimit} className="limit-form">
              <h3>{t('limits.addTitle')}</h3>
              {limitError && <div className="error-message">{limitError}</div>}
              {limitSuccess && <div className="success-inline">{limitSuccess}</div>}
              <div className="limit-form-row">
                <select
                  value={limitForm.category}
                  onChange={(e) => setLimitForm({ ...limitForm, category: e.target.value })}
                  className="limit-select"
                >
                  <option value="">{t('limits.selectCategory')}</option>
                  {['Kira', 'Elektrik', 'Su', 'Doğalgaz', 'İnternet', 'Market', 'Ev Eşyası', 'Temizlik', 'Yemek', 'Diğer'].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={limitForm.limit_amount}
                  onChange={(e) => setLimitForm({ ...limitForm, limit_amount: e.target.value })}
                  placeholder={t('limits.amountPlaceholder')}
                  className="limit-input"
                />
                <button type="submit" className="btn btn-primary">{t('limits.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
